import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Alert,
  Snackbar,
  Paper,
  Grid,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Fade,
  LinearProgress,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  CardActions
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimerIcon from '@mui/icons-material/Timer';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QuizIcon from '@mui/icons-material/Quiz';
import SaveIcon from '@mui/icons-material/Save';
import { useLanguage } from './LanguageContext'; // adjust path as needed

// Helper to format time in hh:mm:ss
function formatTime(seconds) {
  const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');
  return `${hrs}:${mins}:${secs}`;
}

const TakeTestPage = () => {
  const { t } = useLanguage();
  const { quiz_id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [status, setStatus] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [progress, setProgress] = useState({ answers: [] });
  const [elapsedTime, setElapsedTime] = useState(0);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  const progressRef = useRef(progress);
  const elapsedTimeRef = useRef(elapsedTime);
  const timerRef = useRef(null);

  // Sync refs for autosave
  useEffect(() => {
    progressRef.current = progress;
    elapsedTimeRef.current = elapsedTime;
  }, [progress, elapsedTime]);

  // Fetch quiz status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const auth = getAuth();
        const token = await auth.currentUser.getIdToken();
        const url = `${process.env.REACT_APP_BACKEND_URL}/quizzes/status/${quiz_id}`;
        const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = await response.json();
        setStatus(data.status);
      } catch (err) {
        console.error(err);
        setError(t.errorFetchingQuizStatus);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [quiz_id, t.errorFetchingQuizStatus]);

  // Fetch quiz details
  const fetchQuiz = useCallback(async () => {
    setLoading(true);
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/quizzes/take/${quiz_id}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json();
      setQuiz(data);
    } catch (err) {
      console.error(err);
      setError(t.errorFetchingQuiz);
    } finally {
      setLoading(false);
    }
  }, [quiz_id, t.errorFetchingQuiz]);

  // Fetch saved progress
  const fetchProgress = useCallback(async () => {
    setLoading(true);
    try {
      await fetchQuiz();
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/quizzes/progress/${quiz_id}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setProgress(data);
      setElapsedTime(data.elapsedTime || 0);
    } catch (err) {
      console.error(err);
      setError(t.errorFetchingProgress);
    } finally {
      setLoading(false);
    }
  }, [fetchQuiz, quiz_id, t.errorFetchingProgress]);

  // Confirm and start quiz
  const handleConfirm = () => {
    setConfirmation(true);
    if (status === 'In progress') {
      fetchProgress();
    } else {
      fetchQuiz();
    }
  };

  // Final submit
  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/quizzes/take/${quiz_id}`;
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answers: progress.answers || [] }),
      });
      navigate(`/result/${quiz_id}`);
    } catch (err) {
      console.error(err);
      setError(t.errorSubmittingQuiz);
      setSnackbar({ open: true, message: t.errorSubmittingQuiz, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [quiz_id, progress.answers, navigate, t.errorSubmittingQuiz]);

  // Autosave progress
  const autoSaveProgress = useCallback(async () => {
    try {
      setSaving(true);
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/quizzes/progress/${quiz_id}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          answers: progressRef.current.answers || [],
          elapsedTime: elapsedTimeRef.current,
        }),
      });
      if (!response.ok) throw new Error('Error saving progress');
      setSnackbar({ open: true, message: t.progressSaved, severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: t.errorSavingProgress, severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [quiz_id, t.progressSaved, t.errorSavingProgress]);

  // Timer
  useEffect(() => {
    if (confirmation && quiz) {
      timerRef.current = setInterval(() => setElapsedTime(t => t + 1), 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [confirmation, quiz]);

  // Autosave interval
  useEffect(() => {
    if (confirmation && quiz) {
      const id = setInterval(autoSaveProgress, 30000);
      return () => clearInterval(id);
    }
  }, [confirmation, quiz, autoSaveProgress]);

  // Auto-submit when time's up
  useEffect(() => {
    if (quiz && elapsedTime >= quiz.timeLimit * 60) {
      clearInterval(timerRef.current);
      handleSubmit();
    }
  }, [elapsedTime, quiz, handleSubmit]);

  // Handle option change
  const handleOptionChange = (questionId, selectedOption) => {
    setProgress(prev => {
      const answers = [...prev.answers];
      const idx = answers.findIndex(a => a.question_id === questionId);
      if (idx < 0) answers.push({ question_id: questionId, selectedOption });
      else answers[idx].selectedOption = selectedOption;
      return { answers };
    });
  };

  const handleSnackbarClose = (e, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(s => ({ ...s, open: false }));
  };

  const handleNextQuestion = () => {
    if (quiz && currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateProgress = () => {
    if (!quiz) return 0;
    return (progress.answers.length / quiz.questions.length) * 100;
  };

  const gradientBackground = {
    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  };

  if (loading) {
    return (
        <Container sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <QuizIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
              <Typography variant="h6" color="primary" gutterBottom>
                {t.loadingQuiz}
              </Typography>
              <CircularProgress size={24} sx={{ mt: 2 }} />
            </Box>
          </Fade>
        </Container>
    );
  }

  if (error) {
    return (
        <Container sx={{ py: 4 }}>
          <Fade in timeout={800}>
            <Alert severity="error" variant="filled" sx={{ borderRadius: 1, boxShadow: 2 }}>
              {error}
            </Alert>
          </Fade>
        </Container>
    );
  }

  if (status === 'Taken') {
    return (
        <Container sx={{ py: 4 }}>
          <Fade in timeout={800}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
              <Typography variant="h5" fontWeight={450}>
                {t.quizAlreadyCompleted}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t.quizAlreadyCompletedDescription}
              </Typography>
              <Button variant="contained" onClick={() => navigate(`/result/${quiz_id}`)} sx={{ mr: 2, borderRadius: 1, textTransform: 'none', fontWeight: 500 }}>
                {t.viewResults}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/my-class')} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500 }}>
                {t.goToDashboard}
              </Button>
            </Paper>
          </Fade>
        </Container>
    );
  }

  return (
      <Container component="main" maxWidth="lg" sx={{ py: 3 }}>
        <Fade in timeout={800}>
          <Box>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 1, ...gradientBackground, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton color="black" onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" fontWeight={450} color={"black"}>
                  {confirmation && quiz ? quiz.title : t.searchQuiz}
                </Typography>
              </Box>
              {confirmation && quiz && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={t.timeRemaining}>
                      <Chip
                          icon={<TimerIcon />}
                          label={`${t.timeRemaining}: ${formatTime(quiz.timeLimit * 60 - elapsedTime)}`}
                          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 450, mr: isMobile ? 0 : 1 }}
                      />
                    </Tooltip>
                    {!isMobile && (
                        <Tooltip title={t.elapsedTime}>
                          <Chip
                              icon={<HourglassTopIcon />}
                              label={`${t.elapsedTime}: ${formatTime(elapsedTime)}`}
                              sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}
                          />
                        </Tooltip>
                    )}
                  </Box>
              )}
            </Paper>

            {!confirmation ? (
                <Fade in timeout={1000}>
                  <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 24, mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="h5" fontWeight={450}>
                        {t.quizInstructions}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Typography variant="subtitle1" fontWeight={450} gutterBottom>
                          {t.beforeYouBegin}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Chip label="1" size="small" sx={{ mr: 1, bgcolor: theme.palette.primary.main, color: 'white', fontWeight: 450 }} />
                            {t.stableInternetConnection}
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Chip label="2" size="small" sx={{ mr: 1, bgcolor: theme.palette.primary.main, color: 'white', fontWeight: 450 }} />
                            {t.cannotPauseQuiz}
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Chip label="3" size="small" sx={{ mr: 1, bgcolor: theme.palette.primary.main, color: 'white', fontWeight: 450 }} />
                            {t.answerAllQuestions}
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Chip label="4" size="small" sx={{ mr: 1, bgcolor: theme.palette.primary.main, color: 'white', fontWeight: 450 }} />
                            {t.timerStartsOnConfirm}
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Chip label="5" size="small" sx={{ mr: 1, bgcolor: theme.palette.primary.main, color: 'white', fontWeight: 450 }} />
                            {t.autoSaveInfo}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <Button variant="contained" size="large" onClick={handleConfirm} sx={{ px: 4, py: 1, borderRadius: 1, textTransform: 'none', fontWeight: 450, boxShadow: '0 4px 10px rgba(0,0,0,0.15)' }}>
                        {t.understandStartQuiz}
                      </Button>
                    </Box>
                  </Paper>
                </Fade>
            ) : (
                quiz && (
                    <Fade in timeout={1000}>
                      <Box>
                        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 1, bgcolor: theme.palette.background.paper }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" fontWeight={450}>
                              {t.questionsAnswered
                                  .replace('{{answered}}', progress.answers.length)
                                  .replace('{{total}}', quiz.questions.length)}
                            </Typography>
                            <Typography variant="body2" fontWeight={450}>
                              {t.percentComplete.replace('{{percent}}', Math.round(calculateProgress()))}
                            </Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={calculateProgress()} sx={{ height: 6, borderRadius: 3 }} />
                        </Paper>

                        {isMobile && (
                            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Button variant="outlined" onClick={handlePrevQuestion} disabled={currentQuestion === 0} size="small">
                                {t.previous}
                              </Button>
                              <Typography variant="body2" fontWeight={450}>
                                {`${t.question} ${currentQuestion + 1} ${t.outOf} ${quiz.questions.length}`}
                              </Typography>
                              <Button variant="outlined" onClick={handleNextQuestion} disabled={currentQuestion === quiz.questions.length - 1} size="small">
                                {t.next}
                              </Button>
                            </Paper>
                        )}

                        {isMobile ? (
                            <Card elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } }}>
                              <CardHeader title={`${t.question} ${currentQuestion + 1}`} sx={{ bgcolor: theme.palette.primary.light, color: 'white', py: 1.5 }} />
                              <CardContent sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                  {quiz.questions[currentQuestion].question}
                                </Typography>
                                <FormControl component="fieldset" sx={{ width: '100%', mt: 2 }}>
                                  <RadioGroup
                                      value={progress.answers.find(a => a.question_id === quiz.questions[currentQuestion].id)?.selectedOption || ''}
                                      onChange={e => handleOptionChange(quiz.questions[currentQuestion].id, e.target.value)}
                                  >
                                    {quiz.questions[currentQuestion].options.map((opt, i) => (
                                        <FormControlLabel
                                            key={i}
                                            value={opt}
                                            control={<Radio sx={{ color: theme.palette.primary.main, '&.Mui-checked': { color: theme.palette.primary.main } }} />}
                                            label={opt}
                                            sx={{ mb: 1, p: 1, borderRadius: 1, width: '100%', transition: 'background-color 0.2s', '&:hover': { bgcolor: theme.palette.action.hover } }}
                                        />
                                    ))}
                                  </RadioGroup>
                                </FormControl>
                              </CardContent>
                              <CardActions sx={{ p: 2, pt: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                  <Button variant="outlined" onClick={handlePrevQuestion} disabled={currentQuestion === 0}>
                                    {t.previous}
                                  </Button>
                                  <Button variant="contained" onClick={handleNextQuestion} disabled={currentQuestion === quiz.questions.length - 1}>
                                    {t.next}
                                  </Button>
                                </Box>
                              </CardActions>
                            </Card>
                        ) : (
                            quiz.questions.map((q, idx) => (
                                <Fade key={q.id} in timeout={800 + idx * 100}>
                                  <Card elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } }}>
                                    <CardHeader title={`${t.question} ${idx + 1}`} sx={{ bgcolor: theme.palette.primary.light, color: 'white', py: 1.5 }} />
                                    <CardContent sx={{ p: 2 }}>
                                      <Typography variant="h6" gutterBottom>
                                        {q.question}
                                      </Typography>
                                      <FormControl component="fieldset" sx={{ width: '100%', mt: 2 }}>
                                        <RadioGroup
                                            value={progress.answers.find(a => a.question_id === q.id)?.selectedOption || ''}
                                            onChange={e => handleOptionChange(q.id, e.target.value)}
                                        >
                                          <Grid container spacing={1}>
                                            {q.options.map((opt, i) => (
                                                <Grid item xs={12} key={i}>
                                                  <Paper
                                                      elevation={0}
                                                      sx={{
                                                        p: 0,
                                                        border: '1px solid',
                                                        borderColor: progress.answers.find(a => a.question_id === q.id)?.selectedOption === opt
                                                            ? theme.palette.primary.main
                                                            : theme.palette.grey[300],
                                                        borderRadius: 1,
                                                        bgcolor: progress.answers.find(a => a.question_id === q.id)?.selectedOption === opt
                                                            ? `${theme.palette.primary.light}15`
                                                            : 'transparent',
                                                      }}
                                                  >
                                                    <FormControlLabel
                                                        value={opt}
                                                        control={<Radio sx={{ color: theme.palette.primary.main, '&.Mui-checked': { color: theme.palette.primary.main } }} />}
                                                        label={opt}
                                                        sx={{ m: 0, p: 1, width: '100%' }}
                                                    />
                                                  </Paper>
                                                </Grid>
                                            ))}
                                          </Grid>
                                        </RadioGroup>
                                      </FormControl>
                                    </CardContent>
                                  </Card>
                                </Fade>
                            ))
                        )}

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', position: 'sticky', bottom: 16, zIndex: 10 }}>
                          <Button variant="outlined" startIcon={<SaveIcon />} onClick={autoSaveProgress} disabled={saving} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 450 }}>
                            {saving ? t.saving : t.saveProgress}
                          </Button>
                          <Button
                              variant="contained"
                              color="primary"
                              onClick={handleSubmit}
                              disabled={quiz ? progress.answers.length !== quiz.questions.length : true}
                              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 450, px: 3 }}
                          >
                            {t.submitQuiz}
                          </Button>
                        </Box>
                      </Box>
                    </Fade>
                )
            )}
          </Box>
        </Fade>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
  );
};

export default TakeTestPage;
